// src/utils/dayFlags.js
//
// The day-type taxonomy for a time record, and the shared filter predicates
// built on it (PRD_TimeRecordDayFlags C7/C8/C9; formerly lateFilter.js, BL-006).
//
// A record carries at most ONE day-type flag. That is a property of the writers,
// not a convention: leave/absent short-circuit the calc before lateness is
// computed and piece rate forces late to 0, so all three persist isLate:false
// (AddTimeRecordModal.js:296-306, :337-341, :1063, :1148); the three are
// mutually exclusive with each other (:652-669 clears piece rate, and the piece
// rate checkbox is disabled={onLeave || isAbsent} at :2427); and the kiosk
// hardcodes all three off (functions/src/kiosk/recordClockEvent.js:291, :361).
//
// Both surfaces offer the same two ways in — type a token in the search box, or
// pick it from the dropdown — but they filter different things:
//
//   Time Tracking          → the EMPLOYEE list, for the selected day
//   Employee Time Records  → one employee's DATE rows, for the selected month
//
// The tokens and predicates live here rather than being hand-copied into both
// pages: BL-038 exists because exactly that copying let a predicate drift apart
// across four files.
//
// NOTE: `onLeave` is the STRING 'yes'/'no', not a boolean — timeRecordService.js:134
// writes `onLeave || "no"` and the modal passes `onLeave ? "yes" : "no"`. A
// truthiness test flags EVERY record, since 'no' is truthy.

/**
 * The five day-type flags, in `dayFlagOf` resolution order.
 *
 * The order is DEFENSIVE ONLY — the writers make these mutually exclusive, so
 * no engine-produced record can reach the tiebreak. It exists so legacy or
 * hand-edited data degrades to the most consequential label rather than
 * rendering two pills.
 */
export const DAY_FLAGS = {
  absent: { label: 'Absent', test: (r) => r.isAbsent === true },
  'unpaid leave': { label: 'Unpaid Leave', test: (r) => r.onLeave === 'yes' && r.isPaidLeave !== true },
  'paid leave': { label: 'Paid Leave', test: (r) => r.onLeave === 'yes' && r.isPaidLeave === true },
  'piece rate': { label: 'Piece Rate', test: (r) => r.isPieceRate === true },
  late: { label: 'Late', test: (r) => r.isLate === true },
};

/**
 * Filter-only supersets — valid search tokens and dropdown values, but never a
 * `dayFlagOf` result. `leave` matches either leave type; it is what a user
 * naturally types, and it costs one map entry.
 */
export const FILTER_ONLY = {
  leave: { test: (r) => r.onLeave === 'yes' },
};

/** Every valid token. Lookups resolve against this, not DAY_FLAGS. */
const ALL_TOKENS = { ...DAY_FLAGS, ...FILTER_ONLY };

/** The `late` dropdown value and search token — one string, so the two routes can't drift. */
export const LATE_FILTER_TOKEN = 'late';

/**
 * Normalize a raw search term to a token: trimmed, internal whitespace
 * collapsed, lower-cased.
 *
 * The `(term || '')` is load-bearing — callers pass raw input, and the suite
 * asserts `isLateSearchToken(null) === false`. Whitespace collapsing is what
 * makes multi-word tokens work: `.trim()` alone leaves `piece  rate` unmatched.
 */
export const normalizeToken = (term) =>
  (term || '').trim().replace(/\s+/g, ' ').toLowerCase();

/**
 * Every token the normalized term is a PREFIX of.
 *
 * Prefix, not exact: the box filters on every keystroke, so an exact test left
 * a partial token matching nothing — typing `piece rate` blanked the table for
 * nine keystrokes and only filled in on the last one. Prefix lets the list
 * narrow as the user types: `l` → late + leave, `la` → late, `le` → leave.
 *
 * The direction matters — this asks whether a TOKEN starts with the TERM, never
 * the reverse — but be precise about what that does and does not buy:
 *
 *   PREVENTED. A name that merely CONTAINS a token still goes to text matching.
 *   `Slater` and `Laterza` contain "late" but no token starts with them, so the
 *   surname hijack `lateFilter.js:21-23` was written for cannot happen.
 *
 *   ACCEPTED. A name whose LEADING characters prefix a token does flip the box
 *   into flag mode for those keystrokes. On the roster, where this predicate
 *   also gates name/email/ID search (TimeTracking.js:493-501), typing `Abad`
 *   filters by `absent` for `a` and `ab`, then falls back to text at `aba`. The
 *   trade-off was made deliberately at /test-impl: users keep typing, so a
 *   partial term self-corrects, and the alternative — an exact test — blanked
 *   the table for every keystroke of a nine-character token. The real cost is
 *   that a DELIBERATE one- or two-letter name search on the roster is no longer
 *   available.
 *
 * An EMPTY term matches NOTHING, not everything — `''` is a prefix of every
 * string, so without the guard a cleared search box would flip into flag mode,
 * and the dropdown's "All Records" (value `''`) would filter the table down to
 * flagged rows only.
 *
 * Reading own keys off `ALL_TOKENS` also makes the inherited-member trap
 * structural rather than guarded: `constructor` / `toString` can never appear
 * in `Object.keys`, so no lookup can resolve to a value whose `.test` is
 * undefined and throw on a keystroke.
 */
const matchingTokens = (term) => {
  const key = normalizeToken(term);
  if (!key) return [];
  return Object.keys(ALL_TOKENS).filter((name) => name.startsWith(key));
};

/**
 * True when the search box should switch from text matching to flag filtering.
 *
 * @param {string} term - raw search box value
 * @returns {boolean}
 */
export const isSearchToken = (term) => matchingTokens(term).length > 0;

/**
 * True when the search box should switch to LATENESS filtering specifically.
 * Retained for the roster's empty-state reason (`emptyReasonFor`), which stays
 * late-specific.
 *
 * Deliberately EXACT while `isSearchToken` is prefix-based, and the asymmetry is
 * load-bearing: this one decides whether to print "none of the N active
 * employees have a late clock-in". Under a prefix test the term `l` would
 * satisfy it while actually filtering late AND both leave kinds, so the page
 * would state something false about a list it did not build. A partial term
 * falls through to `no-match`, whose wording is true whatever matched.
 *
 * @param {string} term - raw search box value
 * @returns {boolean}
 */
export const isLateSearchToken = (term) => normalizeToken(term) === LATE_FILTER_TOKEN;

/**
 * True when any record in the list matches the given token.
 *
 * A partial term matches the UNION of every token it prefixes, which is what
 * makes typing feel like narrowing rather than guessing: `l` keeps late and
 * both leave kinds, `la` drops to late alone. A complete token prefixes only
 * itself, so the dropdown values — which are exact tokens — resolve to exactly
 * one predicate and are unaffected.
 *
 * An unknown token returns false rather than throwing — a filter that matches
 * nothing is a recoverable UI state; a TypeError is a white screen.
 *
 * @param {Array} records - time records / entries, may be null
 * @param {string} token - a raw, partial, or normalized token
 * @returns {boolean}
 */
export const hasFlaggedRecord = (records, token) => {
  const names = matchingTokens(token);
  if (names.length === 0) return false;
  return (records || []).some(
    (r) => r && names.some((name) => ALL_TOKENS[name].test(r))
  );
};

/**
 * True when any record in the list is flagged late.
 *
 * `isLate` is written by the attendance engine (calculateLateHours > 0 past
 * grace) and is informational only — it is never deducted.
 *
 * @param {Array} records - time records / entries, may be null
 * @returns {boolean}
 */
export const hasLateRecord = (records) => hasFlaggedRecord(records, LATE_FILTER_TOKEN);

/**
 * True when any record in the list carries ANY day-type flag. Drives the FLAGS
 * column sort (C4), which floats every flagged day rather than only late ones.
 *
 * @param {Array} records - time records / entries, may be null
 * @returns {boolean}
 */
export const hasAnyFlaggedRecord = (records) =>
  (records || []).some((r) => r && dayFlagOf(r) !== null);

/**
 * True when any record in the list represents a day actually WORKED — i.e. one
 * that is neither leave nor absent. A piece-rate day counts as worked: the
 * person worked, only the pay basis differs (C5).
 *
 * Shared by the monthly footer's "Days Worked" (C5) and the roster's
 * "Clocked In" (C11), which define the same predicate over different lists.
 * Note this is NOT the negation of `hasFlaggedRecord(records, 'leave')` — a
 * date holding both a worked record and a leave record must count as both.
 *
 * @param {Array} records - time records / entries, may be null
 * @returns {boolean}
 */
export const hasWorkedRecord = (records) =>
  (records || []).some((r) => r && r.onLeave !== 'yes' && r.isAbsent !== true);

/**
 * The single day-type flag a record carries, or null for an ordinary worked day.
 *
 * @param {object} record - a time record, may be null
 * @returns {string|null} a key of DAY_FLAGS
 */
export const dayFlagOf = (record) => {
  if (!record) return null;
  const key = Object.keys(DAY_FLAGS).find((k) => DAY_FLAGS[k].test(record));
  return key || null;
};

/**
 * Why the Time Tracking list came back empty — picks which empty-state message
 * to show. Pure, so it can be tested without a component harness.
 *
 * Deliberately NOT generalized to the new flags: the new tokens fall through to
 * `no-match`, whose wording stays true for them.
 *
 * The three cases are NOT interchangeable, and getting it wrong prints a false
 * statement to the user:
 *
 *  - `no-active`  nobody was employed that day. Blame the date.
 *  - `no-late`    lateness was the ONLY filter and nobody was late. Safe to say
 *                 "none of the N active employees were late".
 *  - `no-match`   anything else. Names both filters, so it stays true whatever
 *                 combination is applied.
 *
 * The `!filterDepartment` guard on `no-late` is load-bearing: with a department
 * ALSO selected we only searched that department, so "none of the N active
 * employees have a late clock-in" would be false whenever someone outside it
 * was late — and N would describe a roster the user is not looking at. That is
 * the same false-statement class this whole empty state exists to fix, so it
 * falls through to `no-match` instead.
 *
 * @param {object} args
 * @param {number} args.activeCount - employees active on the selected date, BEFORE search/department
 * @param {string} args.searchTerm - raw search box value
 * @param {string} args.filterDepartment - selected department ('' when none)
 * @returns {'no-active'|'no-late'|'no-match'}
 */
export const emptyReasonFor = ({ activeCount, searchTerm, filterDepartment }) => {
  if (!activeCount) return 'no-active';
  if (isLateSearchToken(searchTerm) && !filterDepartment) return 'no-late';
  return 'no-match';
};
